// Custom blocks editor — used for "blocks" kind pages. Title comes from the
// studio template (rename propagates automatically). Page ORDER is controlled
// by the studio's page list, not here.
import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import PhotoField from "./PhotoField";

const BLOCK_TYPES = [
  { id: "heading", label: "Heading" },
  { id: "paragraph", label: "Paragraph" },
  { id: "image", label: "Image" },
  { id: "contact", label: "Contact Information" },
  { id: "signature", label: "Signature" },
  { id: "divider", label: "Divider" },
];

export default function BlocksEditor({ pageDef, data, onChange }) {
  const blocks = Array.isArray(data) ? data : [];
  const set = v => onChange(v);
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const arr = [...blocks];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    set(arr);
  };
  const setBlock = (i, k, v) => set(blocks.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  return (
    <Card className="p-5" data-testid={`cc-page-${pageDef.id}`}>
      <div className="mb-4">
        <h3 className="text-lg font-bold" style={{ fontFamily: "'Anek Malayalam', sans-serif" }}>{pageDef.title_ml}</h3>
        <p className="text-xs text-muted-foreground">{pageDef.title_en} · Blocks can be added, edited, reordered and removed</p>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {BLOCK_TYPES.map(t => (
          <Button key={t.id} type="button" variant="outline" size="sm" className="h-8 text-xs"
            onClick={() => set([...blocks, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, type: t.id, text: "", image: "" }])}
            data-testid={`cc-block-add-${t.id}`}>
            <Plus className="w-3.5 h-3.5 mr-1" /> {t.label}
          </Button>
        ))}
      </div>
      <div className="space-y-3">
        {blocks.map((b, i) => (
          <div key={b.id} className="rounded-lg border border-border p-3 bg-muted/20" data-testid={`cc-block-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{b.type}</span>
              <div className="flex gap-1">
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => move(i, -1)} data-testid={`cc-block-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => move(i, 1)} data-testid={`cc-block-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => set(blocks.filter((_, j) => j !== i))} data-testid={`cc-block-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            {(b.type === "heading" || b.type === "signature") && (
              <Input value={b.text || ""} placeholder={b.type === "heading" ? "Heading (Malayalam / English)" : "Name & designation"}
                onChange={e => setBlock(i, "text", e.target.value)} className="bg-white" data-testid={`cc-block-text-${i}`} />
            )}
            {(b.type === "paragraph" || b.type === "contact") && (
              <Textarea rows={3} value={b.text || ""} placeholder="Text…" onChange={e => setBlock(i, "text", e.target.value)} className="bg-white" data-testid={`cc-block-text-${i}`} />
            )}
            {b.type === "image" && <PhotoField value={b.image || ""} onChange={v => setBlock(i, "image", v)} label="Image" aspect={16 / 9} testid={`cc-block-img-${i}`} />}
            {b.type === "signature" && <div className="mt-2"><PhotoField value={b.image || ""} onChange={v => setBlock(i, "image", v)} label="Signature" aspect={3 / 1} testid={`cc-block-sig-${i}`} /></div>}
          </div>
        ))}
        {!blocks.length && <p className="text-sm text-muted-foreground text-center py-4">No blocks yet — add headings, paragraphs, images or contact info.</p>}
      </div>
    </Card>
  );
}
